import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../../axios";
import Api from "../../../Api";
import { 
  Button, Box, TextField, Grid, Typography, 
  Paper, Backdrop, CircularProgress, Divider 
} from "@material-ui/core";
import { findById } from "../../../adminModel/lots";
import { AuctionListLimit, getIDset } from "../../../adminModel/auctions";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useAlert } from "react-alert";
import * as XLSX from "xlsx";

const useStyles = makeStyles((theme) => ({
  backdrop: { 
    zIndex: theme.zIndex.drawer + 1, 
    color: "#fff", 
    flexDirection: "column" 
  },
  lotRow: { 
    marginBottom: 10, 
    padding: 15, 
    borderBottom: "1px solid #eee",
    transition: "background 0.3s",
    "&:hover": { background: "#f9f9f9" }
  },
  actionBox: {
    position: 'sticky',
    bottom: 0,
    background: '#fff',
    padding: 20,
    boxShadow: '0px -2px 10px rgba(0,0,0,0.1)',
    zIndex: 10
  }
}));

export default function BulkFilterView() {
  const classes = useStyles();
  const alert = useAlert();

  const [loading, setloading] = useState(false);
  const [geteventList, setEventList] = useState([]);
  const [auctionID, setAuctionID] = useState("");
  const [setID, setSetID] = useState("");
  const [lotsToUpdate, setLotsToUpdate] = useState([]);
  const [progressMsg, setProgressMsg] = useState("");

  // Load events
  useEffect(() => {
    async function fetchData() {
      try {
        setloading(true);
        const results = await AuctionListLimit();
        setEventList(results.data);
      } catch (e) {
        console.error(e);
      } finally {
        setloading(false);
      }
    }
    fetchData();
  }, []);

  const handlepick = async (e, v) => {
    if (!v) {
      setAuctionID("");
      setSetID("");
      return;
    }
    setAuctionID(v.id);
    const results = await getIDset(v.id);
    if (results && results[0]) setSetID(results[0].id);
  };

  // --- STEP 1: UPLOAD & SEARCH VIA EXCEL ---
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!auctionID) {
      alert.error("Mangyaring pumili muna ng Event.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws);

      setloading(true);
      const foundLots = [];
      
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        setProgressMsg(`Searching Lot ${row.lot_number}...`);
        
        try {
          const res = await findById(row.lot_number, auctionID, setID);
          if (res.data && res.data[0]) {
            const dbData = res.data[0];
            foundLots.push({
              ...dbData,
              // Pre-fill: Excel value priority, then fallback to Database value
              new_reserve: row.reserve !== undefined ? row.reserve : (dbData.reserve_price || ""),
              new_bp: row.bp !== undefined ? row.bp : (dbData.bp || "")
            });
          }
        } catch (err) {
          console.error(`Search error for lot ${row.lot_number}`, err);
        }
      }

      setLotsToUpdate(foundLots);
      setloading(false);
      setProgressMsg("");
      
      if (foundLots.length > 0) {
        alert.success(`Nahanap ang ${foundLots.length} lots. Maaari mo na itong i-update.`);
      } else {
        alert.error("Walang nahanap na lots base sa Excel list.");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  // --- STEP 2: HANDLER PARA SA INPUT CHANGES ---
  const handleInputChange = (index, field, value) => {
    const updated = [...lotsToUpdate];
    updated[index][field] = value;
    setLotsToUpdate(updated);
  };

  // --- STEP 3: FINAL MASS UPDATE ---
  const handleMassUpdate = async () => {
    if (lotsToUpdate.length === 0) return;

    setloading(true);
    let successCount = 0;

    for (let i = 0; i < lotsToUpdate.length; i++) {
      const lot = lotsToUpdate[i];
      setProgressMsg(`Updating Lot ${lot.lot_number}...`);

      const payload = {
        ...lot,
        auction_id: auctionID,
        set_id: setID,
        lot_number: lot.lot_number.toString(),
        reserve: isNaN(parseInt(lot.new_reserve)) ? 0 : parseInt(lot.new_reserve),
        bp: lot.new_bp
      };

      try {
        await api.put(`${Api.request.URL}/lot`, payload);
        successCount++;
      } catch (err) {
        console.error(`Update failed for lot ${lot.lot_number}`, err);
      }
    }

    setloading(false);
    setProgressMsg("");
    alert.success(`Tagumpay! ${successCount} lots ang na-update.`);
    setLotsToUpdate([]); // Clear screen after update
  };

  return (
    <Box p={3}>
      {/* HEADER CONTROLS */}
      <Paper style={{ padding: 20 }}>
        <Typography variant="h5" gutterBottom>Bulk Search & Pre-filled Update</Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item md={5} xs={12}>
            <Autocomplete
              options={geteventList}
              getOptionLabel={(o) => o.auction_name || ""}
              onChange={handlepick}
              renderInput={(params) => (
                <TextField {...params} label="1. Piliin ang Event" variant="outlined" margin="dense" />
              )}
            />
          </Grid>
          <Grid item md={5} xs={12}>
            <input 
              accept=".xlsx, .xls" 
              style={{ display: 'none' }} 
              id="bulk-upload-input" 
              type="file" 
              onChange={handleExcelUpload} 
            />
            <label htmlFor="bulk-upload-input">
              <Button 
                variant="contained" 
                color="secondary" 
                component="span" 
                fullWidth 
                disabled={!auctionID}
                style={{ height: 40 }}
              >
                2. Upload Excel (Lot & Reserve)
              </Button>
            </label>
          </Grid>
          <Grid item md={2} xs={12}>
             <Button variant="text" size="small" onClick={() => setLotsToUpdate([])}>Clear List</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* LOTS DISPLAY LIST */}
      {lotsToUpdate.length > 0 && (
        <Box mt={4} component={Paper} elevation={3} style={{ overflow: 'hidden' }}>
          <Box p={2} bgcolor="#f5f5f5">
            <Typography variant="subtitle1">
              <b>Mga nahanap na Lots ({lotsToUpdate.length})</b>
            </Typography>
          </Box>
          
          <Box p={2} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {lotsToUpdate.map((lot, index) => (
              <Grid container spacing={2} key={index} alignItems="center" className={classes.lotRow}>
                <Grid item md={2} xs={12}>
                  <Typography variant="body1"><b>Lot #{lot.lot_number}</b></Typography>
                  <Typography variant="caption" color="textSecondary" noWrap>
                    {lot.title}
                  </Typography>
                </Grid>
                
                <Grid item md={4} xs={6}>
                  <TextField
                    label="Reserve Price"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={lot.new_reserve}
                    onChange={(e) => handleInputChange(index, 'new_reserve', e.target.value)}
                  />
                </Grid>

                <Grid item md={4} xs={6}>
                  <TextField
                    label="Buyer's Premium"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={lot.new_bp}
                    onChange={(e) => handleInputChange(index, 'new_bp', e.target.value)}
                  />
                </Grid>
                
                <Grid item md={2} xs={12}>
                   <Typography variant="caption" color="primary">
                     Ready for update
                   </Typography>
                </Grid>
              </Grid>
            ))}
          </Box>

          <Box className={classes.actionBox}>
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth 
              size="large"
              onClick={handleMassUpdate}
            >
              SAVE ALL CHANGES ({lotsToUpdate.length} LOTS)
            </Button>
          </Box>
        </Box>
      )}

      {/* LOADING BACKDROP */}
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
        <Box mt={2}>
          <Typography variant="h6">{progressMsg || "Nagloload..."}</Typography>
        </Box>
      </Backdrop>
    </Box>
  );
}